import classNames from 'classnames'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FaRegStar, FaStar } from 'react-icons/fa'
import NewBadge from '../../NewBadge'

export interface NavigationLinkProps {
  href: string
  name: string
  external?: boolean
  closeMenu?: () => void
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void
  newFeature?: boolean
  icon?: React.ComponentType<{ className?: string }>
  showFavoriteButton?: boolean
  isFavorite?: boolean
  onToggleFavorite?: () => void
}

export function NavigationLink({
  href,
  name,
  external,
  closeMenu,
  onClick,
  newFeature,
  icon: Icon,
  showFavoriteButton,
  isFavorite,
  onToggleFavorite,
}: NavigationLinkProps) {
  const router = useRouter()
  const isActive = router.asPath.includes(href)

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    closeMenu?.()
  }

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onToggleFavorite?.()
  }

  const readerHref = getNativeReaderHref(href)

  return (
    <Link
      key={href}
      href={readerHref}
      aria-label={name}
      onClick={handleClick}
      title={name}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={classNames(
        'flex items-center gap-3 w-full rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 group/link',
        'hover:scale-[1.01] active:scale-[0.99]',
        {
          'bg-primary-50 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300 shadow-sm':
            isActive,
          'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-primary-dark-400/50':
            !isActive,
        },
      )}
    >
      {Icon && (
        <Icon
          className={classNames('text-base flex-shrink-0', {
            'text-primary-500 dark:text-primary-400': isActive,
            'text-slate-400 dark:text-slate-500': !isActive,
          })}
        />
      )}
      <span className="flex-1 truncate">{name}</span>
      {(newFeature || showFavoriteButton) && (
        <div className="flex items-center gap-2 flex-shrink-0">
          {newFeature && <NewBadge size="sm" />}
          {showFavoriteButton && (
            <button
              onClick={handleFavoriteClick}
              title={isFavorite ? 'Favorilerden Çıkar' : 'Favorilere Ekle'}
              className={classNames(
                'p-1 rounded-md transition-all duration-200 hover:scale-110 active:scale-95',
                {
                  'text-yellow-500 hover:text-yellow-600 dark:text-yellow-400':
                    isFavorite,
                  'text-gray-300 hover:text-yellow-500 dark:text-gray-600 dark:hover:text-yellow-400':
                    !isFavorite,
                },
              )}
            >
              {isFavorite ? <FaStar size={14} /> : <FaRegStar size={14} />}
            </button>
          )}
        </div>
      )}
    </Link>
  )
}

export default NavigationLink
